import { Location } from "@angular/common";
import { DestroyRef, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, NavigationStart, Route, Router } from "@angular/router";
import { Deeplinks } from "@ionic-native/deeplinks/ngx";
import { BootstrapService } from "app";
import { authGuard, AuthService } from "auth";
import { I18nService } from 'i18n';
import { Invitation, InvitationError, InvitationService } from "invitation";
import { concatMap, distinctUntilChanged, filter, map, Observable, of, startWith, Subscriber, Subscription, TeardownLogic, throwError } from "rxjs";
import { catchError, first, scan, withLatestFrom } from 'rxjs/operators';
import { Logger, Platform, User } from "shared";

type NavigationInput = [string, any?, any?]; // [landingUrl, prevUser, currentUser]

interface NavigationResult {
  url: string;
  state?: {
    logout: boolean;
    token?: string;
    [key: string]: any;
  };
}

@Injectable({
  providedIn: 'root'
})
export class RouteService {

  currentUrl : string | undefined = undefined;
  protected history : string[] = [];
  // ^/sso/<base64url>.<base64url>.<base64url>(end or / or ? or # or next segment)
  // Updated to support both root level and language-prefixed routes (e.g., /login and /en/login)
  protected loginPathRegex = /^\/(?:[a-z]{2}\/)?(?:sso|login)(?:\/([A-Za-z0-9\-_]+(?:\.[A-Za-z0-9\-_]+){0,2}))?\/?(?:$|[\/?#])/
  protected rootPathRegex = /^\/(?:$|[?#])/
  // `quiz` is for the FAC3LESS Card Finder funnel (apps/fac3less /quiz/check-email,
  // /quiz/start, /quiz, /quiz/result, /quiz/checkout, /quiz/payment, /quiz/success).
  // `withdrawal` is the FAC3LESS public withdrawal portal (apps/fac3less /withdrawal).
  // `membership` is the Global Health authenticated purchase wizard
  // (apps/globalhealth /membership/apply) — it's the first non-public route a
  // just-signed-up funnel user hits, so it must be excluded here or RouteService
  // would hijack that first navigation and bounce them to their home URL
  // (dashboard). It stays protected by authGuard regardless.
  // `join` is the Global Health membership funnel's magic-link landing
  // (apps/globalhealth /join/start?token=…). Same two reasons as the legal gate
  // below: without it the NavigationStart handler bounces the arriving member
  // straight back out — to /login, since they have no client session yet — which
  // is precisely what the emailed link is supposed to give them. And recording a
  // single-use token URL as somebody's `homeUrl` would send them back to a spent
  // link on every later visit.
  // `family/join` is Global Health's DNA Family SPECIAL invitation landing
  // (/:lang/family/join/<token>): public, reached logged out, and its token URL
  // must never become a `homeUrl`. Only that path — the protected /family page
  // itself stays an ordinary member page.
  // All safe to add to the shared regex — MNLO and Credo have no such routes.
  protected publicPathRegex = /^\/(?:[a-z]{2}\/)?(?:entry|register|confirm-email|forgot-password|reset-password|confirm-email-and-setup|conference|privacy|imprint|contact|r|quiz|join|membership|withdrawal|family\/join)(?:\/([A-Za-z0-9\-_]+(?:\.[A-Za-z0-9\-_]+){0,2}))?\/?(?:$|[\/?#])/
  protected tosPathRegex = /^\/[a-zA-Z-]+\/tos\/?$/;
  protected profileCompletePathRegex = /^\/[a-zA-Z-]+\/profile\/complete\/?$/;
  // Legal & Compliance Center gate (/:lang/legal/acknowledge). Like profile/complete
  // it is an authenticated route that lives outside the main layout, so it must be
  // excluded from BOTH filters below: otherwise it gets recorded as `homeUrl`, and
  // the NavigationStart handler bounces the user off it to their dashboard — which
  // would silently defeat legalAcknowledgementGuard.
  protected legalAcknowledgePathRegex = /^\/[a-zA-Z-]+\/legal\/acknowledge\/?$/;
  protected invitationPathRegex = /\/([23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{5}(?:-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{5}){4})(?:\/|\?|#|$)/i;

  protected logger = new Logger('RouteService');

  constructor(private router: Router,
              protected location: Location,
              private authService: AuthService,
              private bootstrapService: BootstrapService,
              private invitationService: InvitationService,
              private deeplinks: Deeplinks,
              private platform: Platform,
              private i18nService: I18nService,
              private destroyRef: DestroyRef) {
    this.logger.debug('ctor');
    this.router.events.pipe(
      takeUntilDestroyed(destroyRef),
      filter(event => event instanceof NavigationEnd),
      map(event => event as NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      startWith(this.location.path()),
      distinctUntilChanged(),
      filter(url => !!url)
    )
    .subscribe(url => {
      this.currentUrl = url;
      if (this.authService.isLoggedIn() && 
          !this.loginPathRegex.test(url) &&
          !this.publicPathRegex.test(url) &&
          !this.tosPathRegex.test(url) &&
          !this.profileCompletePathRegex.test(url) &&
          !this.legalAcknowledgePathRegex.test(url) &&
          !this.invitationPathRegex.test(url)) {
        localStorage.setItem('homeUrl', url);
      }
      if (this.history?.[this.history.length-1] != url) {
        this.history.push(url);
      }
    });

    this.router.events.pipe(
      takeUntilDestroyed(destroyRef),
      filter(event => event instanceof NavigationStart),
      filter(event => {
        return !this.rootPathRegex.test(event.url) &&              // Don't redirect from bare root path (public landing page)
               !this.publicPathRegex.test(event.url) &&            // Don't redirect if already on a public page
               !this.profileCompletePathRegex.test(event.url) &&   // or profile complete page
               !this.legalAcknowledgePathRegex.test(event.url)     // or the legal acknowledgement gate
      }),
      map((event: NavigationStart) => event.url),
      first(),
      concatMap((url: string | null) => {
        return this.authService
        .getUser$()
        .pipe(
          distinctUntilChanged((u1, u2) => {
            if (u1?.id !== u2?.id) {
              // different user
              return false;
            } else {
              // same user but if tos consents have changed - emit to trigger proper navigation
              const tos1 = u1?.consents?.['tos'] || [];
              const tos2 = u2?.consents?.['tos'] || [];
              return tos1.length === tos2.length && 
                     tos1.every((val, i) => val === tos2[i]);
            }
          }),    
          scan((acc: (User | null)[], value: User | null) => {     
            return [acc[1], value];
          }, [null, null]),
          map(([prev, current]) => {
            return [url, prev, current] as [string, User | null, User | null];
          }),
          this.navigationMap()
        )
      })
    )
    .subscribe(({url, state}) => {
      const navigate = (next: string, state = {}) => {
        this.logger.debug(`NAVIGATE: ${url ?? ''} -> ${next}`, { state });
        this.router.navigateByUrl(next, { state });
      }
      navigate(url, state);
    });

    this.platform.ready().then(() => {
      if (this.platform.is('hybrid')) {
        this.deeplinks.route({
          '/:token'       : "invitation",
          '/join/:token'  : "invitation",
          '/sso/:token'   : 'login',        // for login from dash
          '/login/:token' : 'login',
          '/chat/:token'  : 'chat',
          '/walletconnect': 'walletconnect' // TODO: this is app-specific should be supplied by the host app
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ 
          next: (match) => {
            // match.$route - the route we matched, which is the matched entry from the arguments to route()
            // match.$args - the args passed in the link
            // match.$link - the full link data
            // example:
            // $route: "join"
            // $args: {invitationCode: "7PKBG-7YBL5-AXGQ5-6PYGJ-Y9NPN"}
            // $link: {path: "/join/7PKBG-7YBL5-AXGQ5-6PYGJ-Y9NPN", queryString: "", fragment: "", host: "www.realizer.app", url: "https://www.realizer.app/join/7PKBG-7YBL5-AXGQ5-6PYGJ-Y9NPN", …}
    
            // window.alert(`DEEPLINK ${JSON.stringify(match)}`);
            this.logger.info('Deeplink match', match);
            const route = match.$route;
            const args = match.$args;
            const link = match.$link;
            const path = link?.path || '/';
            switch (route) {
              case 'invitation': {
                const user = this.authService.getUser();
                const invitationToken = args.token;
                this.invitationService.processInvitationToken(
                  invitationToken, 
                  user?.id
                )
                .pipe(
                  takeUntilDestroyed(this.destroyRef), 
                  catchError((error) => {
                    if (error instanceof InvitationError) {
                      if (error.code === 'ALREADY_ACKNOWLEDGED' || 
                          error.code === 'SELF_INVITATION') {
                        this.logger.warn('Invitation error', error); 
                        return of(error.invitation);
                      }
                    }
                    return throwError(() => error);
                  }),
                  withLatestFrom(this.i18nService.currentLanguage$),
                )
                .subscribe({
                  next: ([invitation, language]: [Invitation | undefined, string]) => {
                    this.logger.debug('Invitation ', invitation);
                    if (invitation?.relationship?.acknowledged) {
                      this.router.navigateByUrl(`/${language}/media/conference/${invitation?.data.id}`);
                    } else {
                    }
                  },
                  error: error => {
                    if (error instanceof InvitationError) {
                      // invitation is invalid or expired
                      this.logger.error('Invitation error', error);
                    } else {
                      // unexpected error - should not happen!
                      this.logger.error('Invitation error', error);
                    }
                  },
                  complete: () => {}
                });
                break;
              }
              case 'login': {
                this.router.navigateByUrl(path);
                break;
              }
              case 'chat': {
                // this.router.navigateByUrl(path);
                break;
              }
            }
          }, 
          error: (nomatch) => {
            // nomatch.$link - the full link data
            this.logger.warn('Deeplink didn\'t match', nomatch);
          }
        })
      }
    });
  }

  getEntryUrl(): string {
    return '/entry';
  }

  getLoginUrl(): string {
    return '/login';
  }

  getTosConsentUrl() {
    return `/${this.i18nService.getLanguage()}/tos`;
  }

  getDefaultUrl(): string {
    return `/${this.i18nService.getLanguage()}`; //`${this.i18nService.getLanguage()}/media/mnlo`;
  }

  getHomeUrl(defaultUrl = this.getDefaultUrl()): string {
    return localStorage.getItem('homeUrl') ?? defaultUrl;
  }

  getCurrentUrl(): string | undefined {
    return this.currentUrl;
  }

  getPreviousUrl(): string | undefined {
    if (this.currentUrl && this.history.length) {
      if (this.currentUrl!=this.history[this.history.length-1]) {
        return this.history[this.history.length-1];
      } else if (this.history.length>=2) {
        return this.history[this.history.length-2];
      }
    }
    return undefined;
  }

  getPreviousPathTrunkUrl(): string | undefined {
    if (this.currentUrl && this.history.length) {
      const comparePathTrunk = (path1: string, path2: string) => {
        const trunk1 = path1?.split('/')[1];
        const trunk2 = path2?.split('/')[1];
        return trunk1?.localeCompare(trunk2);
      }
      const samePathTrunk = (path1: string, path2: string) => comparePathTrunk(path1, path2)==0;
      if (!samePathTrunk(this.currentUrl, this.history[this.history.length-1])) {
        return this.history[this.history.length-1];
      } else if (this.history.length>=2) {
        for (let index=this.history.length-1; index>=0; index--) {
          if (!samePathTrunk(this.history[index], this.history[index-1])) {
            return this.history[index-1];
          }
        }
      }
    }
    return undefined;
  }

  clearHistory(keep?: number) {
    this.history.splice(0, this.history.length - Math.min(Math.max(0, keep ?? 0), this.history.length));
  }

  hasRoute(name: string, authenticated = false): boolean {
    const check = (routes: Route[], hasAuthGuard = false): boolean => {
      for (const r of routes) {
        const hasGuard = authenticated && [r.canActivate, r.canActivateChild, r.canMatch]
          .flat().includes(authGuard);
        const blocked = hasAuthGuard || hasGuard;
        // if (r.path === route.name && r.component === route.component && !blocked) {
        if (r.path === name /*&& !blocked*/) {
          return true;
        }
        if (r.children && check(r.children, blocked)) {
          return true;
        }
      }
      return false;
    };
  
    return check(this.router.config);
  }

  private navigationMap() {
    return (source: Observable<NavigationInput>): Observable<NavigationResult> =>
      new Observable<NavigationResult>((subscriber: Subscriber<NavigationResult>) => {
        let innerSubscription: Subscription | null = null;
        const state = { landingUrl: null as string | null, count: 0 };
        const outerSubscription = source.subscribe({
          next: ([url, prev, current]) => {
            innerSubscription?.unsubscribe();
            innerSubscription = null;
  
            state.count++;
            if (state.count == 1) {
              state.landingUrl = url;
            } 
  
            this.logger.debug(
              `url: ${state.landingUrl}, ` +
              `previousUserId: ${prev?.id}, currentUserId:${current?.id}, ` +
              `navigated: ${this.router.navigated}`
            );
  
            const inner$ = new Observable<NavigationResult>((subscriber) => {
              const logout = !current && !!prev;
              const navigationState: { logout: boolean, token?: string } = { logout };
  
              const loginMatch = state.landingUrl?.match(this.loginPathRegex);
              const loginToken = loginMatch?.[1];
              if (loginToken) {
                // router initialNavigation is performed after the app bootstrap api call is completed
                // at this moment the user may be already authenticated if there was a valid cid cookie in the request
                state.landingUrl = null;
                navigationState.token = loginToken;
                if (current) {
                  // re-authentication required 
                  // (sso token may be for another user)
                  this.authService.logout().subscribe(() => {
                    subscriber.next({ url: this.getLoginUrl(), state: navigationState });
                  });
                } else {
                  subscriber.next({ url: this.getHomeUrl() });
                }
              } else if (current) {
                // user is authenticated
                const hasTosRoute = this.hasRoute('tos');
                const hasTosConsent = current.consents?.['tos']?.length > 0;
                if (hasTosRoute && !hasTosConsent) {
                  subscriber.next({ url: this.getTosConsentUrl() });
                } else {
                  const invitationMatch = state.landingUrl?.match(this.invitationPathRegex);
                  const invitationToken = invitationMatch?.[1];
                  if (invitationToken) {
                    const language = this.i18nService.getLanguage();
                    state.landingUrl = null;
                    subscriber.next({url: this.getHomeUrl()});
                    return this.invitationService.processInvitationToken(
                          invitationToken, 
                          current.id
                        ).subscribe({
                          next: (invitation: Invitation) => {
                            this.logger.debug('Invitation ', invitation);
                            if (invitation?.relationship?.acknowledged) {
                              subscriber.next({url: `/${language}/media/conference/${invitation.data.id}`});
                            } else {
                              // subscriber.next({url: this.getHomeUrl()}); 
                            }
                          },
                          error: error => {
                            if (error instanceof InvitationError) {
                              if (error.code === 'ALREADY_ACKNOWLEDGED' || 
                                  error.code === 'SELF_INVITATION') {
                                this.logger.warn('Invitation error', error); 
                                subscriber.next({url: `/${language}/media/conference/${error.invitation?.data.id}`})
                              } else {
                                this.logger.error('Invitation error', error);
                                // invitation is invalid or expired
                                // subscriber.next({url: this.getHomeUrl()})
                              }
                            } else {
                              // unexpected error - should not happen!
                              this.logger.error('Invitation error', error);
                              // subscriber.next({url: this.getHomeUrl()});
                            }
                          },
                          complete: () => subscriber.complete()
                        });
                  } else {
                    subscriber.next({ url: this.getHomeUrl() });
                  }
                }
              } else {
                // user is not authenticated
                const invitationMatch = state.landingUrl?.match(this.invitationPathRegex);
                const invitationToken = invitationMatch?.[1];
                if (invitationToken) {
                  state.landingUrl = null;                  
                  return this.invitationService.processInvitationToken(
                      invitationToken, 
                      current
                  ).subscribe({
                    next: (invitation: Invitation) => {
                      const event = invitation?.data;
                      if (event?.id) {
                        subscriber.next({ url: `/conference/${event.id}` });
                      } 
                    },
                    error: (error) => {
                      this.logger.error('Invitation error', error);
                      subscriber.next({ url: this.getLoginUrl(), state: navigationState });
                    },
                    complete: () => subscriber.complete()
                  });
                }
                const entry = this.bootstrapService.getSetting('entry');
                if (entry?.media) {
                  subscriber.next({ url: this.getEntryUrl() });
                } else {
                  subscriber.next({ url: this.getLoginUrl(), state: navigationState });
                }
              }
              subscriber.complete();
              return () => {}; // undefined
            });
  
            innerSubscription = inner$.subscribe({
              next: (result: NavigationResult) => subscriber.next(result),
              error: (error) => subscriber.error(error),
              complete: () => { innerSubscription = null; },
            });
          },
          error: (error) => subscriber.error(error),
          complete: () => {
            innerSubscription?.unsubscribe();
            subscriber.complete();
          },
        });
        return (() => {
          innerSubscription?.unsubscribe();
          outerSubscription.unsubscribe();
        }) as TeardownLogic;
      })
  }
}
